/**
 * Each Eve step type as a canvas kind: its name, its icon and its accent
 * (`kind-*` classes in index.css, in GTM's palette; darker in the light
 * theme), and the Steps panel's groups.
 *
 * @module components/workflows/canvas/kinds
 */
import type { WorkflowNodeType } from "@amoreg/contracts";
import { WORKFLOW_STEP_LABEL } from "@repo/shared/workflowEdit";
import {
  BellIcon,
  FileDownIcon,
  FlagIcon,
  FolderInputIcon,
  GitCompareArrowsIcon,
  GlobeIcon,
  ListChecksIcon,
  MailIcon,
  MessageSquarePlusIcon,
  RepeatIcon,
  SparklesIcon,
  TableIcon,
  UserCheckIcon,
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";

import { GitBranchIcon, StepStartIcon } from "./icons";

export interface KindMeta {
  readonly label: string;
  readonly icon: ComponentType<SVGProps<SVGSVGElement>>;
}

export const KINDS: Readonly<Record<WorkflowNodeType, KindMeta>> = {
  start: { label: WORKFLOW_STEP_LABEL.start, icon: StepStartIcon },
  prompt: { label: WORKFLOW_STEP_LABEL.prompt, icon: SparklesIcon },
  condition: { label: WORKFLOW_STEP_LABEL.condition, icon: GitBranchIcon },
  compare: { label: WORKFLOW_STEP_LABEL.compare, icon: GitCompareArrowsIcon },
  "for-each": { label: WORKFLOW_STEP_LABEL["for-each"], icon: RepeatIcon },
  intake: { label: WORKFLOW_STEP_LABEL.intake, icon: FolderInputIcon },
  ingestion: { label: WORKFLOW_STEP_LABEL.ingestion, icon: TableIcon },
  "browser-task": { label: WORKFLOW_STEP_LABEL["browser-task"], icon: GlobeIcon },
  approval: { label: WORKFLOW_STEP_LABEL.approval, icon: UserCheckIcon },
  checklist: { label: WORKFLOW_STEP_LABEL.checklist, icon: ListChecksIcon },
  chat: { label: WORKFLOW_STEP_LABEL.chat, icon: MessageSquarePlusIcon },
  notify: { label: WORKFLOW_STEP_LABEL.notify, icon: BellIcon },
  "save-file": { label: WORKFLOW_STEP_LABEL["save-file"], icon: FileDownIcon },
  email: { label: WORKFLOW_STEP_LABEL.email, icon: MailIcon },
  output: { label: WORKFLOW_STEP_LABEL.output, icon: FlagIcon },
};

/** The Steps panel's groups; Start is placed once, never added. */
export const PALETTE: ReadonlyArray<{ readonly label: string; readonly kinds: ReadonlyArray<WorkflowNodeType> }> = [
  { label: "Gather", kinds: ["intake", "ingestion", "browser-task"] },
  { label: "Think", kinds: ["prompt", "condition", "compare", "for-each"] },
  { label: "People", kinds: ["approval", "checklist"] },
  { label: "Send", kinds: ["chat", "notify", "save-file", "email", "output"] },
];

/** The class that sets `--kind` for a step type. */
export const kindClass = (type: WorkflowNodeType) => `kind-${type}`;
